import React, { useState } from 'react';
import { Star, MapPin, MessageSquare, Video, Clock, Heart, Share2, Award, ChevronDown, Globe, Zap, GraduationCap, ShieldCheck, Crown, Gem, BadgeCheck, Users, ArrowRight, ShoppingBag, FileText, PlayCircle, BookOpen, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Rating } from '../components/ui/Rating';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import { AdSlot } from '../components/ads/AdSlot';
interface AstrologerProfilePageProps {
  onNavigate: (page: string) => void;
}
function BadgePill({
  badge
}: {
  badge: string;
}) {
  let icon: React.ReactNode, text: string, cls: string, tooltip: string;
  if (badge === 'TOP') {
    icon = <Crown className="w-2.5 h-2.5" data-id="element-16814" />;
    text = 'TOP';
    cls = 'bg-amber-100 text-amber-700 border-amber-200';
    tooltip = 'TOP — лучший рейтинг и отзывы';
  } else if (badge === 'PRO') {
    icon = <ShieldCheck className="w-2.5 h-2.5" data-id="element-16815" />;
    text = 'PRO';
    cls = 'bg-indigo-100 text-indigo-700 border-indigo-200';
    tooltip = 'PRO — подтверждённый профессионал';
  } else {
    icon = <Gem className="w-2.5 h-2.5" data-id="element-16816" />;
    text = 'Рек.';
    cls = 'bg-emerald-100 text-emerald-700 border-emerald-200';
    tooltip = 'Рекомендуем — выбор редакции';
  }
  return <div className="relative group/pill inline-flex" data-id="element-16817">
      <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full border cursor-help ${cls}`} data-id="element-16818">
        {icon}
        {text}
      </span>
      <div className="hidden sm:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-slate-800 text-white text-[10px] font-medium rounded-md whitespace-nowrap opacity-0 invisible group-hover/pill:opacity-100 group-hover/pill:visible transition-all duration-150 pointer-events-none z-50 shadow-lg" data-id="element-16819">
        {tooltip}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-16820" />
      </div>
    </div>;
}
function ReviewText({
  text,
  maxLength = 150
}: {
  text: string;
  maxLength?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  if (text.length <= maxLength) return <p className="text-slate-600 text-sm leading-relaxed" data-id="element-16821">{text}</p>;
  return <p className="text-slate-600 text-sm leading-relaxed" data-id="element-16822">
      {expanded ? text : text.slice(0, maxLength) + '...'}
      <button onClick={e => {
      e.stopPropagation();
      setExpanded(!expanded);
    }} className="ml-1 text-indigo-600 hover:text-indigo-700 font-medium text-xs" data-id="element-16823">
        {expanded ? 'Свернуть' : 'Читать далее'}
      </button>
    </p>;
}
export function AstrologerProfilePage({
  onNavigate
}: AstrologerProfilePageProps) {
  const {
    addItem
  } = useCart();
  const [isFavorite, setIsFavorite] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedTime, setSelectedTime] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(9);
  const [currentYear, setCurrentYear] = useState(2025);
  const [shareToast, setShareToast] = useState(false);
  const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  const dayNames = ['Пн', 'Вт', 'Ср', 'Чт'];
  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
    setSelectedDay(0);
  };
  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
    setSelectedDay(0);
  };
  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
    }
    setShareToast(true);
    setTimeout(() => setShareToast(false), 2000);
  };
  const baseDayNum = 14;
  const availableDays = dayNames.map((name, i) => ({
    label: name,
    num: baseDayNum + i
  }));
  const timeSlots = ['10:00', '12:30', '15:00', '18:00'];
  const services = [{
    title: 'Разбор натальной карты',
    price: '5 000 ₽',
    duration: '60 мин',
    format: 'video' as const,
    desc: 'Полный анализ карты: сильные стороны, предназначение, финансы и отношения.'
  }, {
    title: 'Соляр на год',
    price: '4 000 ₽',
    duration: '3 дня',
    format: 'chat' as const,
    desc: 'Текстовый прогноз на год с разбором ключевых сфер жизни.'
  }, {
    title: 'Синастрия (Совместимость)',
    price: '6 000 ₽',
    duration: '90 мин',
    format: 'video' as const,
    desc: 'Анализ совместимости партнеров, точки роста и рекомендации.'
  }];
  const products = [{
    id: 1,
    title: 'Гид «Ретроградный Меркурий»',
    type: 'PDF',
    price: '990 ₽',
    icon: FileText,
    color: 'bg-blue-100 text-blue-600'
  }, {
    id: 2,
    title: 'Вебинар «Натальная карта для начинающих»',
    type: 'Видео',
    price: '2 500 ₽',
    icon: PlayCircle,
    color: 'bg-purple-100 text-purple-600'
  }, {
    id: 3,
    title: 'Рабочая тетрадь «Лунный календарь 2025»',
    type: 'PDF',
    price: '1 200 ₽',
    icon: BookOpen,
    color: 'bg-emerald-100 text-emerald-600'
  }];
  const reviews = [{
    id: 1,
    name: 'Мария К.',
    date: '12 мая 2024',
    service: 'Натальная карта',
    rating: 5,
    text: 'Елена — потрясающий специалист! Очень глубокий разбор, всё по полочкам. Никакой воды, только конкретика. Ушла с полным пониманием, куда двигаться дальше. Обязательно вернусь за соляром!'
  }, {
    id: 2,
    name: 'Алексей Д.',
    date: '3 мая 2024',
    service: 'Синастрия',
    rating: 5,
    text: 'Заказывали с женой синастрию. Елена очень тактично и профессионально разобрала наши сильные и слабые стороны как пары. Многое стало понятнее, появились конкретные инструменты для работы над отношениями. Рекомендую всем парам, даже если кажется что всё хорошо — узнаете много нового.'
  }, {
    id: 3,
    name: 'Ольга В.',
    date: '28 апр 2024',
    service: 'Соляр на год',
    rating: 4,
    text: 'Хороший прогноз, всё структурировано. Единственное — хотелось бы чуть больше деталей по финансовой сфере.'
  }, {
    id: 4,
    name: 'Дмитрий Р.',
    date: '15 апр 2024',
    service: 'Натальная карта',
    rating: 5,
    text: 'Первый раз обращался к астрологу и не пожалел. Елена объясняет простым языком, без эзотерического тумана. Получил чёткие рекомендации по карьере.'
  }, {
    id: 5,
    name: 'Наталья С.',
    date: '2 апр 2024',
    service: 'Детский гороскоп',
    rating: 5,
    text: 'Заказала разбор карты для дочери. Очень полезно — теперь лучше понимаю её характер и как поддержать в учёбе.'
  }];
  const faqs = [{
    id: '1',
    title: 'Как проходит видео-консультация?',
    content: 'Созваниваемся в Zoom/Google Meet. Я заранее готовлю карту. Во время звонка разбираем все аспекты, после — отправляю запись и рекомендации.'
  }, {
    id: '2',
    title: 'Что нужно для подготовки?',
    content: 'Дата, время и место рождения. Если время неизвестно — проведём ректификацию.'
  }, {
    id: '3',
    title: 'Можно ли перенести запись?',
    content: 'Да, не позднее чем за 24 часа. При отмене менее чем за сутки предоплата не возвращается.'
  }];
  const similarAstrologers = [{
    id: 1,
    name: 'Михаил Волков',
    rating: 4.8,
    reviews: 86,
    price: 4500,
    badge: 'PRO',
    avatar: 'bg-amber-100 text-amber-700'
  }, {
    id: 2,
    name: 'Анна Коваль',
    rating: 5.0,
    reviews: 214,
    price: 6000,
    badge: 'TOP',
    avatar: 'bg-emerald-100 text-emerald-700'
  }, {
    id: 3,
    name: 'Дмитрий Орлов',
    rating: 4.9,
    reviews: 152,
    price: 5500,
    badge: 'Featured',
    avatar: 'bg-blue-100 text-blue-700'
  }];
  const visibleReviews = showAllReviews ? reviews : reviews.slice(0, 3);
  return <div className="w-full bg-slate-50 pb-20 min-h-screen" data-id="element-16824">
      <div className="h-40 md:h-52 bg-gradient-to-r from-indigo-500 via-purple-500 to-fuchsia-500 w-full relative" data-id="element-16825">
        <div className="absolute inset-0 bg-black/10" data-id="element-16826" />
        <div className="absolute top-3 left-4 sm:left-6 lg:left-8 z-10" data-id="element-16827">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Астрологи',
          onClick: () => onNavigate('astrologers')
        }, {
          label: 'Елена Смирнова'
        }]} className="text-white/80 [&_span]:text-white [&_button]:text-white/80 [&_button:hover]:text-white [&_svg]:text-white/50" data-id="element-16828" />
        </div>
      </div>

      {shareToast && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg animate-[fadeIn_0.2s_ease-out]" data-id="element-16829">
          Ссылка скопирована ✓
        </div>}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-20" data-id="element-16830">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-16831">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-8 space-y-5" data-id="element-16832">
            {/* Profile Header */}
            <SectionErrorBoundary sectionName="astrologer-hero" data-id="element-16833">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-visible" data-id="element-16834">
                <div className="px-4 sm:px-6" data-id="element-16835">
                  {/* Mobile layout */}
                  <div className="sm:hidden flex flex-col items-center pt-16" data-id="element-16836">
                    <div className="relative mb-3 -mt-28" data-id="element-16837">
                      <div className="w-24 h-24 rounded-2xl bg-white p-1 shadow-lg" data-id="element-16838">
                        <div className="w-full h-full rounded-xl bg-indigo-100 flex items-center justify-center text-3xl font-bold text-indigo-700" data-id="element-16839">
                          ЕС
                        </div>
                      </div>
                      <div className="absolute bottom-1 right-1 w-4 h-4 bg-emerald-500 border-[3px] border-white rounded-full z-20" title="Онлайн" data-id="element-16840" />
                    </div>
                    <div className="text-center mb-2" data-id="element-16841">
                      <h1 className="text-xl font-bold text-slate-900 flex items-center justify-center gap-1.5 mb-1" data-id="element-16842">
                        Елена Смирнова
                        <BadgeCheck className="w-5 h-5 text-blue-500 flex-shrink-0" data-id="element-16843" />
                      </h1>
                      <div className="flex items-center justify-center gap-1.5 mb-2" data-id="element-16844">
                        <BadgePill badge="TOP" data-id="element-16845" />
                        <BadgePill badge="PRO" data-id="element-16846" />
                      </div>
                      <div className="flex items-center justify-center gap-2 text-xs text-slate-500" data-id="element-16847">
                        <span className="flex items-center text-emerald-600 font-medium" data-id="element-16848">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 animate-pulse" data-id="element-16849" />
                          Онлайн
                        </span>
                        <span className="text-slate-300" data-id="element-16850">•</span>
                        <span className="flex items-center" data-id="element-16851">
                          <Zap className="w-3 h-3 text-amber-500 mr-0.5" data-id="element-16852" />
                          ~15 мин
                        </span>
                        <span className="text-slate-300" data-id="element-16853">•</span>
                        <span className="flex items-center" data-id="element-16854">
                          <MapPin className="w-3 h-3 mr-0.5" data-id="element-16855" />
                          Москва
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 w-full mb-4" data-id="element-16856">
                      <button onClick={() => setIsFavorite(!isFavorite)} className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium transition-all ${isFavorite ? 'border-rose-200 bg-rose-50 text-rose-600' : 'border-slate-200 text-slate-600'}`} data-id="element-16857">
                        <Heart className={`w-4 h-4 ${isFavorite ? 'fill-rose-500 text-rose-500' : ''}`} data-id="element-16858" />
                        {isFavorite ? 'В избранном' : 'В избранное'}
                      </button>
                      <button onClick={handleShare} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600" data-id="element-16859">
                        <Share2 className="w-4 h-4" data-id="element-16860" /> Поделиться
                      </button>
                    </div>
                  </div>

                  {/* Desktop layout */}
                  <div className="hidden sm:block pt-20" data-id="element-16861">
                    <div className="flex items-end gap-4 -mt-28 mb-6" data-id="element-16862">
                      <div className="relative flex-shrink-0" data-id="element-16863">
                        <div className="w-28 h-28 rounded-2xl bg-white p-1 shadow-lg" data-id="element-16864">
                          <div className="w-full h-full rounded-xl bg-indigo-100 flex items-center justify-center text-4xl font-bold text-indigo-700" data-id="element-16865">
                            ЕС
                          </div>
                        </div>
                        <div className="absolute bottom-1 right-1 w-4 h-4 bg-emerald-500 border-[3px] border-white rounded-full z-20" title="Онлайн" data-id="element-16866" />
                      </div>
                      <div className="flex-1 min-w-0 pb-1" data-id="element-16867">
                        <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-16868">
                          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-1.5" data-id="element-16869">
                            Елена Смирнова
                            <BadgeCheck className="w-5 h-5 text-blue-500 flex-shrink-0" data-id="element-16870" />
                          </h1>
                          <BadgePill badge="TOP" data-id="element-16871" />
                          <BadgePill badge="PRO" data-id="element-16872" />
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-500" data-id="element-16873">
                          <span className="flex items-center text-emerald-600 font-medium" data-id="element-16874">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 animate-pulse" data-id="element-16875" />
                            Онлайн
                          </span>
                          <span className="text-slate-300" data-id="element-16876">•</span>
                          <span className="flex items-center" data-id="element-16877">
                            <Zap className="w-3 h-3 text-amber-500 mr-0.5" data-id="element-16878" />
                            ~15 мин
                          </span>
                          <span className="text-slate-300" data-id="element-16879">•</span>
                          <span className="flex items-center" data-id="element-16880">
                            <MapPin className="w-3 h-3 mr-0.5" data-id="element-16881" />
                            Москва
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 pb-1 flex-shrink-0" data-id="element-16882">
                        <button onClick={() => setIsFavorite(!isFavorite)} className={`p-2 rounded-lg border transition-all ${isFavorite ? 'border-rose-200 bg-rose-50' : 'border-slate-200 hover:border-rose-200 hover:bg-rose-50'}`} title={isFavorite ? 'Убрать из избранного' : 'В избранное'} data-id="element-16883">
                          <Heart className={`w-4 h-4 ${isFavorite ? 'fill-rose-500 text-rose-500' : 'text-slate-400'}`} data-id="element-16884" />
                        </button>
                        <button onClick={handleShare} className="p-2 rounded-lg border border-slate-200 hover:border-slate-300 transition-all" title="Поделиться" data-id="element-16885">
                          <Share2 className="w-4 h-4 text-slate-400" data-id="element-16886" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Info Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 border-y border-slate-100 mb-4 text-center" data-id="element-16887">
                    <div data-id="element-16888">
                      <div className="flex items-center justify-center gap-1 mb-0.5" data-id="element-16889">
                        <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" data-id="element-16890" />
                        <span className="font-bold text-slate-900 text-sm" data-id="element-16891">
                          4.9
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-500" data-id="element-16892">
                        124 отзыва
                      </span>
                    </div>
                    <div data-id="element-16893">
                      <div className="font-bold text-slate-900 text-sm mb-0.5" data-id="element-16894">
                        8 лет
                      </div>
                      <span className="text-[10px] text-slate-500" data-id="element-16895">Опыт</span>
                    </div>
                    <div data-id="element-16896">
                      <div className="flex items-center justify-center gap-1 mb-0.5" data-id="element-16897">
                        <Globe className="w-3.5 h-3.5 text-slate-400" data-id="element-16898" />
                        <span className="font-bold text-slate-900 text-sm" data-id="element-16899">
                          RU EN
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-500" data-id="element-16900">Языки</span>
                    </div>
                    <div data-id="element-16901">
                      <div className="flex items-center justify-center gap-1.5 mb-0.5" data-id="element-16902">
                        <Video className="w-3.5 h-3.5 text-indigo-500" data-id="element-16903" />
                        <MessageSquare className="w-3.5 h-3.5 text-emerald-500" data-id="element-16904" />
                      </div>
                      <span className="text-[10px] text-slate-500" data-id="element-16905">
                        Форматы
                      </span>
                    </div>
                  </div>

                  {/* Specialties */}
                  <div className="flex flex-wrap gap-1.5 mb-5" data-id="element-16906">
                    {['Западная астрология', 'Натальная карта', 'Прогностика', 'Синастрия', 'Детский гороскоп'].map(tag => <span key={tag} className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium" data-id="element-16907">
                        {tag}
                      </span>)}
                  </div>

                  {/* About — NO CONTACTS BLOCK HERE */}
                  <div className="pb-6" data-id="element-16908">
                    <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-16909">
                      Обо мне
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-2" data-id="element-16910">
                      Привет! Я Елена, профессиональный астролог с 8-летним
                      стажем. Моя цель — перевести сложный язык звёзд на
                      понятный человеческий. Я не пугаю ретроградным Меркурием,
                      а даю конкретные инструменты для улучшения качества жизни.
                    </p>
                    <p className="text-sm text-slate-600 leading-relaxed" data-id="element-16911">
                      Использую методы классической западной астрологии +
                      психологию. Помогла 1000+ клиентам найти призвание,
                      наладить отношения и выйти из кризиса.
                    </p>
                  </div>
                </div>
              </div>
            </SectionErrorBoundary>

            {/* Statistics */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3" data-id="element-16912">
              {[{
              icon: Users,
              value: '1 250+',
              label: 'Консультаций',
              color: 'bg-indigo-100 text-indigo-600'
            }, {
              icon: Heart,
              value: '94%',
              label: 'Повторных',
              color: 'bg-emerald-100 text-emerald-600'
            }, {
              icon: Star,
              value: '5.0',
              label: 'Ср. оценка',
              color: 'bg-amber-100 text-amber-600'
            }].map(stat => <Card key={stat.label} padding="sm" className="flex flex-col sm:flex-row items-center sm:items-center gap-1.5 sm:gap-2.5 text-center sm:text-left" data-id="element-16913">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${stat.color}`} data-id="element-16914">
                    <stat.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-16915" />
                  </div>
                  <div data-id="element-16916">
                    <div className="text-sm sm:text-base font-bold text-slate-900 leading-tight" data-id="element-16917">
                      {stat.value}
                    </div>
                    <div className="text-[9px] sm:text-[10px] text-slate-500 font-medium" data-id="element-16918">
                      {stat.label}
                    </div>
                  </div>
                </Card>)}
            </div>

            {/* Services */}
            <SectionErrorBoundary sectionName="astrologer-services" data-id="element-16919">
              <Card padding="md" data-id="element-16920">
                <h2 className="text-lg font-bold text-slate-900 mb-3" data-id="element-16921">
                  Услуги
                </h2>
                <div className="divide-y divide-slate-100" data-id="element-16922">
                  {services.map((s, i) => <div key={i} className="py-3 first:pt-0 last:pb-0 group" data-id="element-16923">
                      <div className="hidden sm:flex items-center gap-3" data-id="element-16924">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${s.format === 'video' ? 'bg-indigo-50' : 'bg-emerald-50'}`} data-id="element-16925">
                          {s.format === 'video' ? <Video className="w-4 h-4 text-indigo-500" data-id="element-16926" /> : <MessageSquare className="w-4 h-4 text-emerald-500" data-id="element-16927" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-16928">
                          <div className="text-sm font-bold text-slate-900 truncate group-hover:text-indigo-600 transition-colors" data-id="element-16929">
                            {s.title}
                          </div>
                          <div className="flex items-center gap-2 text-[11px] text-slate-500" data-id="element-16930">
                            <span className="flex items-center" data-id="element-16931">
                              <Clock className="w-3 h-3 mr-0.5" data-id="element-16932" />
                              {s.duration}
                            </span>
                            <span data-id="element-16933">•</span>
                            <span data-id="element-16934">
                              {s.format === 'video' ? 'Видео' : 'Чат'}
                            </span>
                          </div>
                        </div>
                        <span className="text-sm font-bold text-indigo-700 whitespace-nowrap" data-id="element-16935">
                          {s.price}
                        </span>
                        <Button variant="primary" size="sm" onClick={() => {
                      addItem({
                        id: `astrologer-service-${i}-${Date.now()}`,
                        name: `${s.title} — Елена Смирнова`,
                        description: s.desc,
                        price: parseInt(s.price.replace(/\s/g, '')),
                        type: 'consultation',
                        format: s.format === 'video' ? 'Видеозвонок' : 'Чат',
                        duration: s.duration,
                        badges: [{
                          label: 'Консультация',
                          variant: 'pro'
                        }]
                      });
                      onNavigate('checkout');
                    }} className="flex-shrink-0 text-xs px-3" data-id="element-16936">
                          Заказать
                        </Button>
                      </div>
                      <div className="sm:hidden" data-id="element-16937">
                        <div className="flex items-start gap-2.5 mb-2" data-id="element-16938">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${s.format === 'video' ? 'bg-indigo-50' : 'bg-emerald-50'}`} data-id="element-16939">
                            {s.format === 'video' ? <Video className="w-3.5 h-3.5 text-indigo-500" data-id="element-16940" /> : <MessageSquare className="w-3.5 h-3.5 text-emerald-500" data-id="element-16941" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-16942">
                            <div className="text-sm font-bold text-slate-900" data-id="element-16943">
                              {s.title}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5" data-id="element-16944">
                              <span className="flex items-center" data-id="element-16945">
                                <Clock className="w-3 h-3 mr-0.5" data-id="element-16946" />
                                {s.duration}
                              </span>
                              <span data-id="element-16947">•</span>
                              <span data-id="element-16948">
                                {s.format === 'video' ? 'Видео' : 'Чат'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between ml-[38px]" data-id="element-16949">
                          <span className="text-sm font-bold text-indigo-700" data-id="element-16950">
                            {s.price}
                          </span>
                          <Button variant="primary" size="sm" onClick={() => {
                        addItem({
                          id: `astrologer-service-${i}-${Date.now()}`,
                          name: `${s.title} — Елена Смирнова`,
                          description: s.desc,
                          price: parseInt(s.price.replace(/\s/g, '')),
                          type: 'consultation',
                          format: s.format === 'video' ? 'Видеозвонок' : 'Чат',
                          duration: s.duration,
                          badges: [{
                            label: 'Консультация',
                            variant: 'pro'
                          }]
                        });
                        onNavigate('checkout');
                      }} className="text-xs px-3" data-id="element-16951">
                            Заказать
                          </Button>
                        </div>
                      </div>
                    </div>)}
                </div>
              </Card>
            </SectionErrorBoundary>

            {/* Products */}
            <Card padding="md" data-id="element-16952">
              <div className="flex items-center justify-between mb-3" data-id="element-16953">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-1.5" data-id="element-16954">
                  <ShoppingBag className="w-4 h-4 text-indigo-500" data-id="element-16955" />
                  Товары
                </h2>
                <button className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center" onClick={() => onNavigate('product-detail')} data-id="element-16956">
                  Все <ChevronRight className="w-3 h-3 ml-0.5" data-id="element-16957" />
                </button>
              </div>
              <div className="divide-y divide-slate-100" data-id="element-16958">
                {products.map(p => <div key={p.id} className="flex items-center gap-2.5 py-2.5 first:pt-0 last:pb-0 cursor-pointer hover:bg-slate-50 rounded-lg transition-colors" onClick={() => onNavigate('product-detail')} data-id="element-16959">
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${p.color}`} data-id="element-16960">
                      <p.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-16961" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-16962">
                      <div className="text-xs sm:text-sm font-medium text-slate-900 truncate" data-id="element-16963">
                        {p.title}
                      </div>
                      <span className="text-[10px] text-slate-400 uppercase" data-id="element-16964">
                        {p.type}
                      </span>
                    </div>
                    <span className="text-xs sm:text-sm font-bold text-slate-900 whitespace-nowrap flex-shrink-0" data-id="element-16965">
                      {p.price}
                    </span>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" data-id="element-16966" />
                  </div>)}
              </div>
            </Card>

            {/* Education */}
            <Card padding="md" data-id="element-16967">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-16968">
                <GraduationCap className="w-5 h-5 text-indigo-500" data-id="element-16969" />
                Образование
              </h2>
              <div className="space-y-3" data-id="element-16970">
                {[{
                title: 'Московская Академия Астрологии',
                desc: 'Базовый и продвинутый курс • 2016-2018'
              }, {
                title: 'Школа Прогностики',
                desc: 'Глубокая прогностика и соляры • 2019'
              }].map((edu, i) => <div key={i} className="flex gap-3 items-start" data-id="element-16971">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-16972">
                      <Award className="w-4 h-4 text-slate-400" data-id="element-16973" />
                    </div>
                    <div data-id="element-16974">
                      <h4 className="text-sm font-bold text-slate-900" data-id="element-16975">
                        {edu.title}
                      </h4>
                      <p className="text-xs text-slate-500" data-id="element-16976">{edu.desc}</p>
                    </div>
                  </div>)}
              </div>
            </Card>

            {/* Reviews */}
            <SectionErrorBoundary sectionName="astrologer-reviews" data-id="element-16977">
              <Card padding="md" data-id="element-16978">
                <div className="flex items-center justify-between mb-4" data-id="element-16979">
                  <h2 className="text-lg font-bold text-slate-900" data-id="element-16980">
                    Отзывы{' '}
                    <span className="text-slate-400 text-sm font-normal" data-id="element-16981">
                      (124)
                    </span>
                  </h2>
                  <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-1 rounded-lg" data-id="element-16982">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" data-id="element-16983" />
                    <span className="text-sm font-bold text-amber-700" data-id="element-16984">
                      4.9
                    </span>
                  </div>
                </div>
                <div className="space-y-4" data-id="element-16985">
                  {visibleReviews.map(r => <div key={r.id} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0" data-id="element-16986">
                      <div className="flex items-start justify-between mb-2" data-id="element-16987">
                        <div className="flex items-center gap-2.5" data-id="element-16988">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xs font-bold text-indigo-700" data-id="element-16989">
                            {r.name[0]}
                          </div>
                          <div data-id="element-16990">
                            <div className="text-sm font-bold text-slate-900" data-id="element-16991">
                              {r.name}
                            </div>
                            <div className="text-[10px] text-slate-500" data-id="element-16992">
                              {r.date} • {r.service}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5" data-id="element-16993">
                          {Array.from({
                        length: 5
                      }).map((_, i) => <Star key={i} className={`w-3 h-3 ${i < r.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-16994" />)}
                        </div>
                      </div>
                      <ReviewText text={r.text} maxLength={120} data-id="element-16995" />
                    </div>)}
                </div>
                {reviews.length > 3 && <Button variant="outline" size="sm" fullWidth className="mt-4" onClick={() => setShowAllReviews(!showAllReviews)} data-id="element-16996">
                    {showAllReviews ? 'Свернуть' : `Показать ещё ${reviews.length - 3} отзывов`}
                  </Button>}
              </Card>
            </SectionErrorBoundary>

            {/* FAQ */}
            <Card padding="md" data-id="element-16997">
              <h2 className="text-lg font-bold text-slate-900 mb-4" data-id="element-16998">
                Частые вопросы
              </h2>
              <Accordion items={faqs} data-id="element-16999" />
            </Card>
          </div>

          {/* RIGHT COLUMN: SIDEBAR */}
          <div className="lg:col-span-4" data-id="element-17000">
            <div className="sticky top-20 space-y-4" data-id="element-17001">
              {/* Booking Widget */}
              <Card padding="md" className="border-indigo-100 shadow-lg shadow-indigo-900/5" data-id="element-17002">
                <h3 className="text-base font-bold text-slate-900 mb-1" data-id="element-17003">
                  Запись на консультацию
                </h3>
                <div className="flex items-center gap-1.5 mb-4 pb-3 border-b border-slate-100" data-id="element-17004">
                  <span className="text-xs text-slate-500" data-id="element-17005">Стоимость:</span>
                  <span className="text-sm font-bold text-slate-900" data-id="element-17006">
                    от 4 000 до 6 000 ₽
                  </span>
                </div>
                <p className="text-xs text-slate-500 mb-3 font-medium" data-id="element-17007">
                  Выберите дату (МСК)
                </p>
                <div className="flex justify-between items-center mb-3 bg-slate-50 rounded-lg px-2 py-1.5" data-id="element-17008">
                  <button onClick={handlePrevMonth} className="p-0.5 text-slate-400 hover:text-slate-900 transition-colors" data-id="element-17009">
                    <ChevronDown className="w-4 h-4 rotate-90" data-id="element-17010" />
                  </button>
                  <span className="text-sm font-bold text-slate-900" data-id="element-17011">
                    {monthNames[currentMonth]} {currentYear}
                  </span>
                  <button onClick={handleNextMonth} className="p-0.5 text-slate-400 hover:text-slate-900 transition-colors" data-id="element-17012">
                    <ChevronDown className="w-4 h-4 -rotate-90" data-id="element-17013" />
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-1.5 mb-4" data-id="element-17014">
                  {availableDays.map((day, i) => <div key={day.num} onClick={() => setSelectedDay(i)} className={`text-center py-2 px-1 rounded-lg border cursor-pointer transition-all ${i === selectedDay ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 hover:border-indigo-200 text-slate-600'}`} data-id="element-17015">
                      <div className="text-[9px] uppercase tracking-wider font-semibold" data-id="element-17016">
                        {day.label}
                      </div>
                      <div className="font-bold text-base" data-id="element-17017">{day.num}</div>
                    </div>)}
                </div>
                <div className="mb-5" data-id="element-17018">
                  <div className="text-xs font-bold text-slate-900 mb-2 flex items-center" data-id="element-17019">
                    <Clock className="w-3.5 h-3.5 mr-1 text-slate-400" data-id="element-17020" />
                    Время:
                  </div>
                  <div className="grid grid-cols-2 gap-1.5" data-id="element-17021">
                    {timeSlots.map((time, i) => <button key={time} onClick={() => setSelectedTime(i)} className={`py-2 border rounded-lg text-sm font-medium transition-all ${i === selectedTime ? 'border-indigo-500 bg-indigo-500 text-white shadow-sm' : 'border-slate-200 text-slate-700 hover:border-indigo-300'}`} data-id="element-17022">
                        {time}
                      </button>)}
                  </div>
                </div>
                <Button variant="primary" size="lg" fullWidth onClick={() => {
                addItem({
                  id: `astrologer-booking-${Date.now()}`,
                  name: 'Консультация — Елена Смирнова',
                  description: `Онлайн-консультация, ${availableDays[selectedDay]?.label} ${availableDays[selectedDay]?.num} ${monthNames[currentMonth]}, ${timeSlots[selectedTime]}`,
                  price: 5000,
                  type: 'consultation',
                  format: 'Видеозвонок',
                  duration: '60 минут',
                  badges: [{
                    label: 'Консультация',
                    variant: 'pro'
                  }]
                });
                onNavigate('checkout');
              }} data-id="element-17023">
                  Продолжить оформление
                </Button>
                <div className="mt-3 flex items-center justify-center text-[11px] font-medium text-slate-500 bg-slate-50 py-1.5 rounded-lg" data-id="element-17024">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1 text-emerald-500" data-id="element-17025" />
                  Безопасная сделка
                </div>
              </Card>

              {/* Location */}
              <Card padding="sm" className="bg-slate-50 border-none" data-id="element-17026">
                <div className="flex items-center gap-2.5" data-id="element-17027">
                  <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-17028">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" data-id="element-17029" />
                  </div>
                  <div data-id="element-17030">
                    <div className="text-sm font-bold text-slate-900" data-id="element-17031">
                      Москва
                    </div>
                    <div className="text-[11px] text-slate-500" data-id="element-17032">
                      Онлайн по всему миру
                    </div>
                  </div>
                </div>
              </Card>

              {/* Contacts — ONLY in sidebar */}
              <Card padding="sm" data-id="element-17033">
                <h3 className="text-sm font-bold text-slate-900 mb-3" data-id="element-17034">
                  Контакты
                </h3>
                <div className="space-y-2" data-id="element-17035">
                  <a href="https://t.me/elena_astro" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors group" data-id="element-17036">
                    <span className="w-6 h-6 rounded-md bg-sky-100 flex items-center justify-center text-[9px] font-bold text-sky-600 flex-shrink-0" data-id="element-17037">
                      TG
                    </span>
                    <span className="text-xs font-medium text-slate-700 group-hover:text-sky-600 transition-colors" data-id="element-17038">
                      @elena_astro
                    </span>
                    <ExternalLink className="w-3 h-3 text-slate-300 ml-auto" data-id="element-17039" />
                  </a>
                  <a href="https://wa.me/79991234567" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors group" data-id="element-17040">
                    <span className="w-6 h-6 rounded-md bg-emerald-100 flex items-center justify-center text-[9px] font-bold text-emerald-600 flex-shrink-0" data-id="element-17041">
                      WA
                    </span>
                    <span className="text-xs font-medium text-slate-700 group-hover:text-emerald-600 transition-colors" data-id="element-17042">
                      WhatsApp
                    </span>
                    <ExternalLink className="w-3 h-3 text-slate-300 ml-auto" data-id="element-17043" />
                  </a>
                  <a href="https://instagram.com/elena_stars" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors group" data-id="element-17044">
                    <span className="w-6 h-6 rounded-md bg-pink-100 flex items-center justify-center text-[9px] font-bold text-pink-600 flex-shrink-0" data-id="element-17045">
                      IG
                    </span>
                    <span className="text-xs font-medium text-slate-700 group-hover:text-pink-600 transition-colors" data-id="element-17046">
                      elena_stars
                    </span>
                    <ExternalLink className="w-3 h-3 text-slate-300 ml-auto" data-id="element-17047" />
                  </a>
                  <a href="https://elena-astro.ru" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors group" data-id="element-17048">
                    <span className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-17049">
                      <Globe className="w-3 h-3 text-slate-500" data-id="element-17050" />
                    </span>
                    <span className="text-xs font-medium text-slate-700 group-hover:text-indigo-600 transition-colors" data-id="element-17051">
                      elena-astro.ru
                    </span>
                    <ExternalLink className="w-3 h-3 text-slate-300 ml-auto" data-id="element-17052" />
                  </a>
                  <a href="mailto:elena@astro.ru" className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors group" data-id="element-17053">
                    <span className="w-6 h-6 rounded-md bg-blue-100 flex items-center justify-center text-[9px] font-bold text-blue-600 flex-shrink-0" data-id="element-17054">
                      @
                    </span>
                    <span className="text-xs font-medium text-slate-700 group-hover:text-blue-600 transition-colors" data-id="element-17055">
                      elena@astro.ru
                    </span>
                  </a>
                </div>
              </Card>
            </div>
          </div>
        </div>

        {/* Ad Banner */}
        <div className="mt-8" data-id="element-17056">
          <AdSlot slotId="SLOT-12" onNavigate={onNavigate} data-id="element-17057" />
        </div>

        {/* Similar Astrologers */}
        <div className="mt-12 border-t border-slate-200 pt-8" data-id="element-17058">
          <div className="flex items-center justify-between mb-5" data-id="element-17059">
            <h2 className="text-lg font-bold text-slate-900" data-id="element-17060">
              Похожие астрологи
            </h2>
            <button onClick={() => onNavigate('astrologers')} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium hidden sm:flex items-center" data-id="element-17061">
              Все <ArrowRight className="w-3 h-3 ml-0.5" data-id="element-17062" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-17063">
            {similarAstrologers.map(a => <Card key={a.id} padding="sm" hoverable onClick={() => onNavigate('astrologer-profile')} data-id="element-17064">
                <div className="flex items-center gap-3" data-id="element-17065">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${a.avatar}`} data-id="element-17066">
                    {a.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-17067">
                    <div className="flex items-center gap-1.5 mb-0.5" data-id="element-17068">
                      <span className="text-sm font-bold text-slate-900 truncate" data-id="element-17069">
                        {a.name}
                      </span>
                      <BadgePill badge={a.badge} data-id="element-17070" />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500" data-id="element-17071">
                      <span className="flex items-center gap-0.5" data-id="element-17072">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-17073" />
                        {a.rating}
                      </span>
                      <span data-id="element-17074">от {a.price.toLocaleString('ru-RU')} ₽</span>
                    </div>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </div>
    </div>;
}